import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "../context/GameContext.jsx";
import { useLanguage } from "../hooks/useLanguage.js";
import { fadeUp } from "../animations/variants.js";
import { Btn } from "../components/ui/Btn.jsx";
import { Panel, SectionTitle } from "../components/ui/Panel.jsx";
import { LangToggle } from "../components/ui/LangToggle.jsx";
import { ScrollNav } from "../components/ui/ScrollNav.jsx";
import { DraggablePlayerList } from "../components/ui/DraggablePlayerList.jsx";
import { SettingsSheet } from "../components/settings/SettingsSheet.jsx";

export function HomeScreen() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  const handleAdd = () => {
    const n = newName.trim();
    if (n && !state.savedPlayers.some(p => p.name.toLowerCase() === n.toLowerCase())) {
      dispatch({ type: "ADD_PLAYER", payload: n });
      setNewName("");
    }
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleRename = (id) => {
    const n = editName.trim();
    if (n) dispatch({ type: "RENAME_PLAYER", payload: { id, name: n } });
    setEditingId(null);
  };

  const canStart = state.savedPlayers.length >= 4;

  return (
    <motion.div variants={fadeUp} initial="initial" animate="animate" exit="exit"
      className="min-h-screen bg-gradient-to-b from-stone-950 via-amber-950 to-stone-950 flex flex-col">

      <div className="px-4 pt-12 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-amber-200 font-bold text-2xl leading-tight">🌙 Les Loups-Garous</h1>
            <p className="text-amber-700/60 text-xs tracking-widest uppercase mt-0.5">de Thiercelieux · Village Master</p>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <button onClick={() => dispatch({ type: "GO_TO_DOCS" })}
              className="bg-amber-950/60 border border-amber-800/40 rounded-full px-3 py-1.5 text-amber-400 text-xs font-semibold active:scale-95 transition-all">📖</button>
            <button onClick={() => setShowSettings(true)}
              className="bg-amber-950/60 border border-amber-800/40 rounded-full px-3 py-1.5 text-amber-400 text-xs font-semibold active:scale-95 transition-all">⚙️</button>
            <LangToggle />
          </div>
        </div>
        <p className="text-amber-700/40 text-xs italic mt-2">
          {t("La nuit tombe sur le village de Thiercelieux…", "Night falls over the village of Thiercelieux…")}
        </p>
      </div>

      <div ref={scrollRef} className="flex-1 px-4 overflow-y-auto pb-32">
        <Panel className="mb-4">
          <SectionTitle>{t("👥 Les Habitants du Village", "👥 Village Inhabitants")}</SectionTitle>
          <div className="flex gap-2 mb-4">
            <input
              ref={inputRef}
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAdd()}
              maxLength={20}
              placeholder={t("Nom du joueur…", "Player name…")}
              autoComplete="off"
              className="flex-1 bg-amber-950/50 border border-amber-800/40 rounded-xl px-3 py-2.5
                text-amber-100 placeholder-amber-800 text-sm focus:outline-none focus:border-amber-600 transition-all"
            />
            <Btn variant="primary" onClick={handleAdd} className="px-4">＋</Btn>
          </div>

          {state.savedPlayers.length === 0 && (
            <p className="text-amber-800/50 text-sm text-center py-6 italic">
              {t("Ajoutez des joueurs pour commencer…", "Add players to get started…")}
            </p>
          )}

          <DraggablePlayerList players={state.savedPlayers} renderRow={player => (
            editingId === player.id ? (
              <div className="flex items-center gap-2">
                <input autoFocus value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") handleRename(player.id);
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  maxLength={20}
                  className="flex-1 bg-amber-900/60 border border-amber-500/60 rounded-xl px-3 py-2 text-amber-100 text-sm focus:outline-none"
                />
                <button onClick={() => handleRename(player.id)} className="text-emerald-400 w-8 h-8 flex items-center justify-center text-lg active:scale-95">✓</button>
                <button onClick={() => setEditingId(null)} className="text-amber-600 w-8 h-8 flex items-center justify-center active:scale-95">✕</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-amber-950/40 border border-amber-900/20 rounded-xl px-3 py-2.5">
                  <span className="text-amber-100 text-sm">{player.name}</span>
                </div>
                <button onClick={() => { setEditingId(player.id); setEditName(player.name); }} className="text-amber-700 w-8 h-8 flex items-center justify-center text-sm active:scale-95">✏️</button>
                <button onClick={() => dispatch({ type: "REMOVE_PLAYER", payload: player.id })} className="text-red-800 w-8 h-8 flex items-center justify-center text-sm active:scale-95">🗑️</button>
              </div>
            )
          )} />

          {!canStart && state.savedPlayers.length > 0 && (
            <p className="text-amber-800/50 text-xs text-center mt-3 italic">
              {t(`Encore ${4 - state.savedPlayers.length} joueur(s) requis`, `${4 - state.savedPlayers.length} more player(s) required`)}
            </p>
          )}
        </Panel>
      </div>

      <ScrollNav scrollRef={scrollRef} />

      <div className="fixed bottom-0 left-0 right-0 px-4 pb-8 pt-4 bg-gradient-to-t from-stone-950 via-stone-950/90 to-transparent">
        <Btn variant="primary" onClick={() => dispatch({ type: "GO_TO_SETUP" })} disabled={!canStart} size="lg" className="w-full">
          {t("⚔️  Nouvelle Partie", "⚔️  New Game")}
        </Btn>
      </div>

      <AnimatePresence>
        {showSettings && <SettingsSheet onClose={() => setShowSettings(false)} />}
      </AnimatePresence>
    </motion.div>
  );
}
